import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api.service';
import StatCard from '../../components/StatCard';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await apiService.getStats();
      setStats(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { title: 'Manage Visitors', icon: '👥', path: '/admin/visitors', color: 'primary' },
    { title: 'Manage Blogs', icon: '📝', path: '/admin/blogs', color: 'success' },
    { title: 'Manage Galleries', icon: '🖼️', path: '/admin/galleries', color: 'warning' },
    { title: 'Manage Exhibitions', icon: '🎨', path: '/admin/exhibitions', color: 'info' },
  ];

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="loading-spinner">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h2>Admin Dashboard</h2>
        <p className="dashboard-subtitle">Welcome back! Here's an overview of your system</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <StatCard
          title="Total Visitors"
          value={stats?.visitors || 0}
          icon="👥"
          color="primary"
          subtitle="Registered members"
        />
        <StatCard
          title="Blog Posts"
          value={stats?.blogs || 0}
          icon="📝"
          color="success"
          subtitle="Published articles"
        />
        <StatCard
          title="Gallery Categories"
          value={stats?.galleries || 0}
          icon="🖼️"
          color="warning"
          subtitle="Photo collections"
        />
        <StatCard
          title="Exhibitions"
          value={stats?.exhibitions || 0}
          icon="🎨"
          color="info"
          subtitle="Events scheduled"
        />
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <h3>Quick Actions</h3>
        <div className="quick-actions-grid">
          {quickActions.map((action, index) => (
            <button
              key={index}
              className={`quick-action-btn quick-action-${action.color}`}
              onClick={() => navigate(action.path)}
            >
              <span className="quick-action-icon">{action.icon}</span>
              <span className="quick-action-title">{action.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="recent-activity-section">
        <div className="recent-section">
          <h3>Recent Visitors</h3>
          <div className="recent-list">
            {stats?.recent_visitors?.length > 0 ? (
              stats.recent_visitors.map((visitor) => (
                <div key={visitor.memberid} className="recent-item">
                  <div className="recent-item-icon">👤</div>
                  <div className="recent-item-content">
                    <p className="recent-item-title">{visitor.member_name}</p>
                    <small className="recent-item-subtitle">{visitor.member_email}</small>
                  </div>
                  <small className="recent-item-date">
                    #{visitor.memberid}
                  </small>
                </div>
              ))
            ) : (
              <p className="no-data">No recent visitors</p>
            )}
          </div>
        </div>

        <div className="recent-section">
          <h3>Recent Blog Posts</h3>
          <div className="recent-list">
            {stats?.recent_blogs?.length > 0 ? (
              stats.recent_blogs.map((blog) => (
                <div key={blog.blog_id} className="recent-item">
                  <div className="recent-item-icon">📝</div>
                  <div className="recent-item-content">
                    <p className="recent-item-title">{blog.blog_title}</p>
                    <small className="recent-item-subtitle">By {blog.blog_by}</small>
                  </div>
                  <small className="recent-item-date">
                    {blog.uploaded_date ? new Date(blog.uploaded_date).toLocaleDateString() : 'N/A'}
                  </small>
                </div>
              ))
            ) : (
              <p className="no-data">No recent blog posts</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
